import { Button, useNotify } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { StyledButton } from "../../../components/ListShowButton";
import { useField } from "react-final-form";
import { find, findIndex } from "lodash";
import Autocomplete from "@material-ui/lab/Autocomplete";
import SanitizedGrid from "../../../components/SanitizedGrid";
import {
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import AiLoading from "./AiLoading";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  autocompleteInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

interface MoveRouteLocationButtonProps {
  order: number;
  routesData: IRoute[];
  school: ISchool;
  busSchedule: IBusSchedule;
  maxStopTime: number;
}

const MoveRouteLocationButton: React.FC<MoveRouteLocationButtonProps> = ({
  order,
  routesData,
  school,
  busSchedule,
  maxStopTime,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRouteLocation, setSelectedRouteLocation] = useState<any>(null);
  const [selectedRoute, setSelectedRoute] = useState<any>(null);

  const routeIdField = useField("id");
  const routeLocationsField = useField("routeLocations");
  const routeGeometriesField = useField(`routeGeometries`);
  const routeLocationsMovedField = useField(`routeLocationsMoved`);

  const classes = useStyles();
  const notify = useNotify();

  const handleOpenDialog = () => {
    const findRouteLocation = find(routeLocationsField.input.value, [
      "order",
      order,
    ]);
    setSelectedRouteLocation(findRouteLocation);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveMoveRouteLocation = async () => {
    try {
      setIsLoading(true);
      selectedRouteLocation.id = 0;

      routeLocationsMovedField.input.onChange([
        ...routeLocationsMovedField.input.value,
        { routeName: selectedRoute.routeName, location: selectedRouteLocation },
      ]);

      const findRouteLocationIndex = findIndex(
        routeLocationsField.input.value,
        ["order", selectedRouteLocation.order],
      );

      const routeLocationWithoutSchool = removeSchoolLocationFromRouteLocation(
        [
          ...routeLocationsField.input.value.slice(0, findRouteLocationIndex),
          ...routeLocationsField.input.value.slice(findRouteLocationIndex + 1),
        ],
        busSchedule.type,
      );

      const optimisedRouteLocations = await reOptimiseRouteLocation(
        school,
        busSchedule,
        maxStopTime,
        routeLocationWithoutSchool,
      );

      routeLocationsField.input.onChange(optimisedRouteLocations);
      routeGeometriesField.input.onChange([]);
      setIsLoading(false);
      handleClose();
    } catch (error) {
      notify("Failed to move route location", "error");
      setIsLoading(false);
    }
  };

  return (
    <>
      <StyledButton variant="outlined" onClick={handleOpenDialog}>
        Move to another route
      </StyledButton>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Move {selectedRouteLocation?.address} to another route
        </DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Move to</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.autocompleteInput}
                freeSolo
                disableClearable
                options={routesData.filter(
                  (option: any) => option.id !== routeIdField.input.value,
                )}
                getOptionLabel={(option: any) => option.routeName}
                renderOption={(option) => {
                  return (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>{option.routeName} - </span>
                      <div
                        style={{
                          backgroundColor: option.routeColor?.colorTwo,
                          width: "20px",
                          height: "20px",
                          borderRadius: "5px",
                          marginLeft: "5px",
                        }}
                      />
                    </div>
                  );
                }}
                onChange={(_event, value: any) => {
                  setSelectedRoute(
                    find(routesData, ["routeName", value.routeName]),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Existing Routes"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleSaveMoveRouteLocation}
            color="primary"
            label="Save"
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default MoveRouteLocationButton;
